import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { Details } from './Details';
import { StyledTableHeaderCell } from 'views/TirePressure/Components/NotificationTable.styles';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import type {
  SquaresAlert,
  AlertData,
  AlertFeedbackPayload,
} from 'types/squares.type';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const CostView = ({
  enableDarkTheme,
  notification,
  submitFeedback,
}: {
  enableDarkTheme: boolean;
  notification: SquaresAlert;
  submitFeedback: (
    notificationId: string,
    userFeedback: AlertFeedbackPayload
  ) => void;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const fontColor = palette.textPrimary;

  const costInputSx = {
    width: 90,

    '& input': {
      fontSize: '12px',
      color: fontColor,
      padding: '6px 8px',
    },

    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: palette.cardBorderColor,
      },
      '&:hover fieldset': {
        borderColor: palette.cardBorderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.cardBorderColor,
      },
    },
  };

  const [costs, setCosts] = useState<
    Record<
      string,
      {
        partCost: number;
        labourCost: number;
      }
    >
  >({});

  const [feedbackText, setFeedbackText] = useState<string>('');

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  useEffect(() => {
    if (!notification?.data) return;

    const initial = notification?.data?.reduce((acc: any, row: AlertData) => {
      acc[row.work_order_number] = {
        partCost: row.part_cost ?? 0,
        labourCost: row.labour_cost ?? 0,
        totalCost: row.total_cost ?? 0,
      };
      return acc;
    }, {});

    setCosts(initial);
  }, [notification?.data]);

  const updateCost = (
    workOrder: string,
    field: 'partCost' | 'labourCost',
    value: string
  ) => {
    setCosts((prev) => ({
      ...prev,
      [workOrder]: {
        ...prev[workOrder],
        [field]: Number(value),
      },
    }));
  };

  function handleSubmit() {
    submitFeedback(notification.notificationID, {
      comment: feedbackText,
      woData: costs,
    });
  }

  return (
    <Box>
      <Details enableDarkTheme={enableDarkTheme} notification={notification} />
      <Typography
        sx={{
          fontSize: '12px',
          color: ThemePalette.typography.grey,
          fontWeight: '600',
        }}
      >
        {t('description')}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          color: fontColor,
          marginTop: '10px',
        }}
        dangerouslySetInnerHTML={{ __html: notification.message }}
      />

      <Typography
        sx={{
          fontSize: '12px',
          color: ThemePalette.typography.grey,
          marginTop: '40px',
          fontWeight: '600',
        }}
      >
        {t('repeated_work_orders')}
      </Typography>
      <Box
        sx={{
          marginTop: '20px',
        }}
      >
        {notification?.data?.length > 0 ? (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('work_order_number')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('start_date')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('description')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('part_cost')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('labour_cost')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('total_cost')}
                    </StyledTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notification?.data?.map((row: AlertData, index: number) => {
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          '& td': {
                            border: 'none',
                            borderBottom: '1px solid #2F445E',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: fontColor,
                            }}
                          >
                            {row.work_order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: fontColor,
                            }}
                          >
                            {convertUtcToDisplayTime(
                              row.date_of_orders,
                              preferredTimeDisplayMode,
                              timezone,
                              DATE_TIME_FORMAT
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: fontColor,
                            }}
                          >
                            {row.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={costs[row.work_order_number]?.partCost ?? ''}
                            onChange={(e) => {
                              updateCost(
                                row.work_order_number,
                                'partCost',
                                e.target.value
                              );
                            }}
                            sx={costInputSx}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              costs[row.work_order_number]?.labourCost ?? ''
                            }
                            onChange={(e) => {
                              updateCost(
                                row.work_order_number,
                                'labourCost',
                                e.target.value
                              );
                            }}
                            sx={costInputSx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{ fontSize: '12px', color: fontColor }}
                          >
                            {(
                              (costs[row.work_order_number]?.partCost ?? 0) +
                              (costs[row.work_order_number]?.labourCost ?? 0)
                            ).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              sx={{
                marginTop: '10px',
                fontSize: '12px',
                color: ThemePalette.typography.grey,
              }}
            >
              {t(
                'note_total_cost_will_auto_calculate_based_on_part_and_labour'
              )}
            </Typography>

            <TextField
              value={feedbackText}
              onChange={(e) => {
                setFeedbackText(e.target.value);
              }}
              variant="outlined"
              size="small"
              placeholder={t(
                'please_provide_details_regarding_cost_correction'
              )}
              multiline
              minRows={2}
              maxRows={6}
              InputProps={{
                sx: {
                  color: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                  '& textarea': {
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#38475E',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#38475E',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#38475E',
                  },
                  borderRadius: '4px',
                  marginTop: '10px',
                },
              }}
              InputLabelProps={{
                sx: {
                  color: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                  '&.Mui-focused': {
                    color: enableDarkTheme
                      ? ThemePalette.typography.wheat
                      : ThemePalette.typography.black,
                  },
                },
              }}
              fullWidth
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                sx={{
                  marginTop: '10px',
                  textTransform: 'none',
                  fontSize: '12px',
                  backgroundColor: '#1565c0',
                  color: ThemePalette.typography.white,
                  border: '1px solid #2F445E',
                  padding: '6px 20px',
                }}
                onClick={handleSubmit}
              >
                {t('submit_updates')}
              </Button>
            </Box>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: '14px',
              color: ThemePalette.typography.grey,
            }}
          >
            {t('no_repeated_work_orders')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
